import React, { MutableRefObject, useRef, useState } from "react";

import { User } from "chq";
import { Button } from "@culturehq/components";
import { Trans, t } from "app/utils";

import AcceptPolicies from "components/login/AcceptPolicies";
import ExclamationIcon from "components/common/ExclamationIcon";
import Posting from "components/posting/Posting";

import Tip from "components/Tip";
import { useIsMobile } from "app/hooks";
import EmailValidation from "./EmailValidation";
import MoreUserInfoForm from "./MoreUserInfoForm";
import { usePostStoryContext } from "../../contexts/PostStoryContext";
import { UserInformationState, UserInformationValues } from "../../types";

const ExistingUserError = () => (
  <Tip
    message={(
      <>
        ⚠️{" "}
        <strong className="WelcomeBack">
          Your user can not answer this prompt, please use another email!
        </strong>
      </>
    )}
  />
);

const HelloMessageTip = ({ creator }: {creator: User}) => (
  <Tip
    message={(
      <>
        👋{" "}
        <strong className="WelcomeBack">
          Welcome back, {creator.name}! You&apos;re all set to submit your story!
        </strong>
      </>
    )}
  />
);

const DisableMessageTip = () => (
  <Tip
    message={(
      <>
        ⚠️{" "}
        <strong className="WelcomeBack">
          Your user was disabled by an admin, you can not create stories!
        </strong>
      </>
    )}
  />
);

const PoliciesMustBeSignedTip = () => (
  <Tip
    message={(
      <strong>
        Looks like you&apos;re almost done confirming your profile.
        Please review and accept these policies to submit your story!
      </strong>
    )}
    SvgIcon={<ExclamationIcon />}
  />
);

const status = {
  newUser: "new_user",
  disabledUser: "disabled_user",
  privacyPolicyUnsigned: "privacy_policy_unsigned",
  privacyPolicySigned: "privacy_policy_signed"
};

const UserInformationForm = () => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const isMobile = useIsMobile();
  const {
    state: { selectedPrompt },
    setState: setPostStoryContextState,
    handleSubmit
  } = usePostStoryContext();

  const [state, _setState] = useState<UserInformationState>({
    email: undefined,
    error: undefined,
    userAccessStatus: undefined,
    agreed: false,
    policiesAgreed: [],
    creator: undefined,
    organization: { privacyPolicies: [] },
    posting: false
  });

  const [values, _setValues] = useState<UserInformationValues>({
    name: undefined,
    firstName: undefined,
    lastName: undefined,
    avatarUrl: undefined,
    title: undefined,
    locationName: undefined,
    departmentNames: undefined
  });

  const setValues = (
    newState: UserInformationValues) => _setValues(prev => ({ ...prev, ...newState }));
  const setState = (
    newState: Partial<UserInformationState>) => _setState(prev => ({ ...prev, ...newState }));

  const handleAgreeChange = (agreed: boolean) => {
    setState({ agreed });
  };

  const handlePrivacyPolicyChange = (value: boolean, index: number) => {
    const policies = [...state.policiesAgreed];
    policies[index] = value;
    setState({ policiesAgreed: policies });
  };

  const handleStorySubmit = () => {
    if (!state.error && !isDisabledUser) {
      setState({ posting: true });
      handleSubmit({ newPublicUser: { ...values, email: state.email } });
    }
  };

  const handleBack = () => setPostStoryContextState({ userInformationRequest: false });

  const handleUserAccessStatusFetched = (
    newState: Partial<UserInformationState>
  ) => {
    setState(newState);
    if (
      buttonRef.current
      && newState.userAccessStatus === "privacy_policy_signed"
    ) buttonRef.current.focus();
  };

  const isNewUser = state.userAccessStatus === status.newUser;
  const isDisabledUser = state.userAccessStatus === status.disabledUser;
  const isPrivacyPolicyUnsigned = state.userAccessStatus === status.privacyPolicyUnsigned;
  const isPrivacyPolicySigned = state.userAccessStatus === status.privacyPolicySigned;

  const submitButtonLabel = isMobile ? "Post" : "Post Story";

  const { privacyPolicies = [] } = state.organization;
  const isAllPoliciesChecked = privacyPolicies.length === state.policiesAgreed.filter(
    p => p).length;

  return (
    <div className="UserInformation">
      {state.posting ? (
        <Posting />
      ) : (
        <div className="Content">
          {!isMobile && (
            <h2>
              <Trans>Let&apos;s confirm your details</Trans>
            </h2>
          )}

          <div className="UserInformation-Fields">
            <EmailValidation
              selectedPrompt={selectedPrompt}
              onUserAccessStatusFetched={handleUserAccessStatusFetched}
            />

            {isDisabledUser && <DisableMessageTip />}

            {isPrivacyPolicySigned && state.creator && (
              <HelloMessageTip creator={state.creator} />
            )}

            {state.error && <ExistingUserError />}

            {isNewUser && (
              <MoreUserInfoForm
                values={values}
                agreed={state.agreed}
                isAllPoliciesChecked={isAllPoliciesChecked}
                policiesAgreed={state.policiesAgreed}
                submitButtonLabel={submitButtonLabel}
                organization={state.organization}
                onChange={setValues}
                onStorySubmit={handleStorySubmit}
                onAgreeChange={handleAgreeChange}
                onPrivacyPolicyChange={handlePrivacyPolicyChange}
              />
            )}

            {isPrivacyPolicyUnsigned && (
              <>
                <PoliciesMustBeSignedTip />
                <div className="Policies">
                  <AcceptPolicies
                    agreed={state.agreed}
                    policiesAgreed={state.policiesAgreed}
                    organization={state.organization}
                    onAgreeChange={handleAgreeChange}
                    onPrivacyPolicyChange={handlePrivacyPolicyChange}
                  />
                </div>
                {!isNewUser && (
                  <Button
                    className="Submit"
                    primary
                    disabled={!state.agreed || !isAllPoliciesChecked}
                    onClick={handleStorySubmit}
                  >
                    {submitButtonLabel}
                  </Button>
                )}
              </>
            )}
          </div>

          {(!state.userAccessStatus
            || isPrivacyPolicySigned
            || isDisabledUser
            || state.error) && (
            <Button
              ref={buttonRef}
              className="Submit"
              primary
              disabled={!isPrivacyPolicySigned || isDisabledUser || state.error}
              onClick={handleStorySubmit}
            >
              {submitButtonLabel}
            </Button>
          )}

          {!isMobile && (
            <Button
              aria-label={t("Back to the story information")}
              inverted
              className="Back"
              primary
              onClick={handleBack}
            >
              Back
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default UserInformationForm;
